/** 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 * Tek kaynak qubepro-core; yalnız resmî UI senkronuyla dağıtılır.
 */
import { Children, forwardRef, useEffect, useRef, useState, type HTMLAttributes, type ReactNode } from 'react';
import { useWorkspaceFocusMode, WorkspaceFocusModeToggle } from './WorkspaceFocusMode';
import { Icon } from './ComponentIcon';
import { useTableSelectionScope } from './TableSelectionScope';
import './DataTable.css';
import './DataTableTools.css';

export interface TableQuickSearchProps {
    value: string;
    onChange: (value: string) => void;
    label: string;
    placeholder?: string;
    maxLength?: number;
    className?: string;
}

/** Uses the corporate tooltip host on pointer/focus, including touch focus. */
export function TableInformationButton({ label, description }: { label: string; description: string }) {
    return <button type="button" className="datatable-icon-btn" aria-label={label}
        title={description} data-qp-tooltip={description}>
        <Icon name="info" size={18} />
    </button>;
}

/** Shared compact search for SmartFilter and server-paginated system panels. */
export function TableQuickSearch({ value, onChange, label, placeholder = label, maxLength,
    className = 'datatable-toolbar-search datatable-toolbar-search--compact' }: TableQuickSearchProps) {
    const input = useRef<HTMLInputElement>(null);
    const [open, setOpen] = useState(false);
    useEffect(() => {
        const element = input.current;
        if (!element) return;
        const clearNativeSearch = () => onChange(element.value);
        element.addEventListener('search', clearNativeSearch);
        return () => element.removeEventListener('search', clearNativeSearch);
    }, [onChange]);
    useEffect(() => {
        if (!open) return;
        const closeOutsideTools = (event: Event) => {
            const tools = input.current?.closest('.datatable-toolbar') ?? input.current?.parentElement;
            if (event.target instanceof Node && !tools?.contains(event.target)) setOpen(false);
        };
        document.addEventListener('pointerdown', closeOutsideTools);
        document.addEventListener('focusin', closeOutsideTools);
        return () => {
            document.removeEventListener('pointerdown', closeOutsideTools);
            document.removeEventListener('focusin', closeOutsideTools);
        };
    }, [open]);
    return <div className={`${className}${open ? ' is-search-open' : ''}${value.trim() ? ' has-value' : ''}`}
        title={label} onClick={() => input.current?.focus()}>
        <Icon name="search" size={16} />
        <input ref={input} type="search" className="datatable-toolbar-input" value={value} maxLength={maxLength}
            aria-label={label} placeholder={placeholder} onChange={event => onChange(event.target.value)}
            onFocus={() => setOpen(true)} onBlur={event => {
                // Keep the pointer target stable while clicking a neighbouring tool.
                const tools = event.currentTarget.closest('.datatable-toolbar');
                if (!(event.relatedTarget instanceof Node) || !tools?.contains(event.relatedTarget)) setOpen(false);
            }}
            onKeyDown={event => {
                if (event.key !== 'Escape') return;
                event.preventDefault();
                event.stopPropagation();
                if (value) onChange('');
                setOpen(false);
                event.currentTarget.blur();
            }} />
    </div>;
}

/** Host-owned actions; Core owns their order, sizing and accessible button chrome. */
export interface TableToolAction {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    pressed?: boolean;
    expanded?: boolean;
    hasPopup?: HTMLAttributes<HTMLButtonElement>['aria-haspopup'];
}
export interface TableToolsConfiguration {
    selection?: TableToolAction;
    selectionActions?: ReactNode;
    add?: TableToolAction;
    search?: TableQuickSearchProps;
    filterControls?: ReactNode;
    filters?: TableToolAction;
    clear?: TableToolAction;
    actions?: Array<TableToolAction & { icon: string; showLabel?: boolean }>;
    /** Controlled focus used by RecordsTableView; otherwise the surface owns focus. */
    focus?: ReactNode;
    refresh?: TableToolAction;
    settings?: TableToolAction;
}
export function TableToolButton({ action, icon, className = '', showLabel = false }: {
    action: TableToolAction; icon: string; className?: string; showLabel?: boolean;
}) {
    return <button type="button" className={`${showLabel ? 'btn-secondary' : 'datatable-icon-btn'} ${className}`.trim()}
        title={action.label} aria-label={action.label} disabled={action.disabled}
        aria-pressed={action.pressed} aria-expanded={action.expanded} aria-haspopup={action.hasPopup} onClick={action.onClick}>
        <Icon name={icon} size={18} />{showLabel && <span>{action.label}</span>}
    </button>;
}
const ToolButton = TableToolButton;

/** Presentation only. Search, filters, permissions and available operations belong to the host. */
export interface TableToolsSurfaceProps extends HTMLAttributes<HTMLDivElement> {
    tools?: TableToolsConfiguration;
    focusControl?: boolean;
    focusLabels?: { focus: string; fullscreen: string; exit: string };
}
export const TableToolsSurface = forwardRef<HTMLDivElement, TableToolsSurfaceProps>(
    function TableToolsSurface({ className, children, tools, focusControl = true, focusLabels = { focus: 'Focus', fullscreen: 'Fullscreen', exit: 'Exit focus' }, ...props }, ref) {
        const selectionScope = useTableSelectionScope();
        const selection = tools?.selection ? null : selectionScope;
        const toolbar = useRef<HTMLDivElement | null>(null);
        const target = useRef<HTMLElement | null>(null);
        const [available, setAvailable] = useState(false);
        const focus = useWorkspaceFocusMode({ targetRef: target, companionRef: toolbar, shortcutKey: focusControl ? 'f' : '' });
        useEffect(() => {
            const page = toolbar.current?.closest('.qp-system-page');
            const table = page?.querySelector<HTMLElement>('.qp-system-table-stack, .qp-system-table-view');
            target.current = table ?? null;
            setAvailable(Boolean(focusControl && table && !toolbar.current?.closest('[role="dialog"]')));
        }, [children, tools, focusControl]);
        const hasExtension = Children.toArray(children).length > 0;
        const focusButton = available ? <WorkspaceFocusModeToggle className="datatable-icon-btn"
            mode={focus.level} enterLabel={focusLabels.focus} fullscreenLabel={focusLabels.fullscreen}
            exitLabel={focusLabels.exit} onToggle={focus.advance}>
            <Icon name={focus.isFullscreen ? 'focusModeExit' : 'focusModeEnter'} size={18} />
        </WorkspaceFocusModeToggle> : null;
        return <div {...props} ref={element => { toolbar.current = element; if (typeof ref === 'function') ref(element); else if (ref) ref.current = element; }} data-no-row-click="true"
            className={['datatable-toolbar', tools && 'datatable-toolbar--structured', className].filter(Boolean).join(' ')}>
            {tools ? <>
                <div className="datatable-toolbar-left">
                    {(tools.selection || selection) && <ToolButton action={tools.selection ?? {
                        label: selection!.labels.toggle, pressed: selection!.active, onClick: selection!.toggle,
                        disabled: !selection!.available && !selection!.active,
                    }} icon={(tools.selection?.pressed ?? selection?.active) ? 'selectRowsChecked' : 'selectRows'} className="datatable-selection-mode-btn" />}
                    {selection?.active && selection.keys.length > 0 && <>
                        <span className="datatable-bulk-count" role="status">{selection.labels.selectedCount.replace('{count}', String(selection.keys.length))}</span>
                        <ToolButton action={{ label: selection.labels.clear, onClick: selection.clear }} icon="clear" />
                    </>}
                    {tools.selectionActions}
                    {tools.add && <ToolButton action={tools.add} icon="add" className="datatable-toolbar-add-btn" />}
                    {tools.search && <TableQuickSearch {...tools.search} />}
                    {tools.filterControls}
                    {tools.filters && <ToolButton action={tools.filters} icon="filter" />}
                    {tools.clear && !tools.clear.disabled && <ToolButton action={tools.clear} icon="clear" className="datatable-toolbar-clear-btn" />}
                </div>
                <div className="datatable-toolbar-actions">
                    {tools.actions?.map(action => <ToolButton key={action.label} action={action} icon={action.icon} showLabel={action.showLabel} />)}
                    {tools.focus ?? focusButton}
                    {tools.refresh && <ToolButton action={tools.refresh} icon="refresh" />}
                    {tools.settings && <ToolButton action={tools.settings} icon="settings" />}
                </div>
                {hasExtension && <div className="datatable-toolbar-extension">{children}</div>}
            </> : <>{children}{focusButton && <div className="datatable-toolbar-actions">{focusButton}</div>}</>}
        </div>;
    },
);
